const EXTENSION_CONTENT_TYPES = {
    ".pdf": "application/pdf",
    ".jpg": "image/jpeg",
    ".jpeg": "image/jpeg",
    ".png": "image/png",
    ".gif": "image/gif",
};

// Resolves a content type from a file extension (e.g. ".PDF"). Attachment API responses
// don't always include a contentType info field, so callers should fall back to this
// instead of a generic "application/octet-stream" — browsers won't render/open inline
// content they can't identify as a known type (e.g. a PDF), forcing an anonymous download
// even when the browser is configured to open that file type normally.
export const contentTypeFromExtension = (fileExtension, fallback = "application/octet-stream") =>
    EXTENSION_CONTENT_TYPES[String(fileExtension || "").toLowerCase()] || fallback;

export const base64ToBlob = (base64, contentType = "application/octet-stream") => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
};

export const downloadBlobFile = (blob, fileName) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
};

// Displays the blob in a new browser tab (e.g. the native PDF viewer, which still offers
// download/print) instead of forcing an immediate file download.
//
// Uses the standard technique for this (same one FileSaver.js and most production apps
// rely on): a real <a> element with target="_blank" and NO download attribute, clicked
// synthetically. This is deliberately NOT window.open()+navigate — that combination has
// two separate failure modes seen in practice: (1) blob: URLs created in the opener often
// fail to load when navigated-to from a distinct window/tab handle (browsers' blob-URL
// partitioning), and (2) data: URI navigation is frequently intercepted by the browser as
// an anonymous forced download (no filename/extension) instead of being rendered inline.
// A real anchor click does not hit either path — the browser treats it as a normal
// link-follow into its native viewer for the content type, and (unlike window.open) a
// synthetic click on a real link isn't treated as a popup, so no "open blank tab first"
// workaround is needed even when called after an async fetch.
//
// Pass `fileName` to give the resource a real name instead of a random-looking GUID in
// Chrome's Downloads history (a plain Blob has no name; wrapping it in a File does).
export const viewBlobFile = (blob, fileName = null) => {
    const namedBlob = fileName ? new File([blob], fileName, { type: blob.type }) : blob;
    const blobUrl = window.URL.createObjectURL(namedBlob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.target = "_blank";
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoking immediately can race the new tab's load, so only free it up
    // after giving it time to load and cache the resource.
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
};
